// La comisión de un vendedor en una semana: los chips y "Por qué llegó a este monto". Las partes
// suman el neto: cada regla con su parte del multiplicador, umbrales, renta por regla, ajustes y clawback.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { factor } from './ruleText';
import { orEmpty } from './orEmpty';

type Breakdown = RetailCommission.CommissionSellerBreakdown;
type Contribution = RetailCommission.CommissionRuleContribution;

/** «3 piezas cumplieron» o, en una regla que cuenta por venta, «3 ventas cumplieron». */
function metText(r: Contribution): string {
    const one = r.unitsCount === 1;
    if (r.countBy === CommissionCountByEnum.SALE)
        return one ? 'venta cumplió' : 'ventas cumplieron';
    return one ? 'pieza cumplió' : 'piezas cumplieron';
}

export interface WhyItem {
    key: string;
    label: string;
    detail: string;
    amountCents: number;
    tone: 'rule' | 'threshold' | 'rent' | 'adjustment' | 'clawback';
}

export function whyItems(b: Breakdown): WhyItem[] {
    const rules = [...orEmpty(b.byRule)]
        .map((r) => ({ r, amountCents: r.bonusCents + r.multiplierShareCents }))
        .sort((x, y) => y.amountCents - x.amountCents)
        .map(
            ({ r, amountCents }): WhyItem => ({
                key: `rule-${r.ruleId}`,
                label: r.name,
                detail: `${String(r.unitsCount)} ${metText(r)} esta regla`,
                amountCents,
                tone: 'rule',
            }),
        );
    const thresholds = orEmpty(b.thresholds).map(
        (t): WhyItem => ({
            key: `threshold-${t.ruleId}`,
            label: t.name,
            detail: 'Cumplió la condición del corte',
            amountCents: t.amountCents,
            tone: 'threshold',
        }),
    );
    const extra: WhyItem[] = [];
    const rentRules = b.rentByRule ?? [];
    if (rentRules.length > 0) {
        for (const rent of rentRules) {
            extra.push({
                key: `rent-${rent.ruleId}`,
                label: rent.name,
                detail: `${String(rent.couponsCount)} ${rent.couponsCount === 1 ? 'cuota cobrada' : 'cuotas cobradas'} de créditos que originó`,
                amountCents: rent.amountCents,
                tone: 'rent',
            });
        }
    } else if (b.portfolioRentCents !== 0) {
        // Cortes de antes de la renta por regla: una sola línea.
        extra.push({
            key: 'rent',
            label: 'Renta de cartera',
            detail: 'Cupones pagados de créditos que originó',
            amountCents: b.portfolioRentCents,
            tone: 'rent',
        });
    }
    // El clawback ya está dentro de los ajustes: se separa para que se vea.
    const manual = b.adjustmentsCents + b.clawbackCents;
    if (manual !== 0) {
        extra.push({
            key: 'adjustments',
            label: 'Ajustes',
            detail: 'Ajustes manuales y ventas tardías',
            amountCents: manual,
            tone: 'adjustment',
        });
    }
    if (b.clawbackCents > 0) {
        extra.push({
            key: 'clawback',
            label: 'Clawback',
            detail: 'Ventas de cortes cerrados que se cancelaron o devolvieron',
            amountCents: -b.clawbackCents,
            tone: 'clawback',
        });
    }
    return [...rules, ...thresholds, ...extra];
}

export function weekChips(b: Breakdown): string[] {
    const rules = orEmpty(b.byRule).length + orEmpty(b.thresholds).length;
    const chips = [
        `${String(b.salesCount)} ${b.salesCount === 1 ? 'venta' : 'ventas'}`,
        `${String(rules)} ${rules === 1 ? 'regla activada' : 'reglas activadas'}`,
    ];
    if (b.multiplierBps > 10_000) chips.push(`${factor(b.multiplierBps)} acelerador`);
    if (b.clawbackCents > 0) chips.push(`− ${formatMoneyCents(b.clawbackCents)} clawback`);
    // Sin reglas que usen el NPI llega null: no hay dato que mostrar.
    if (b.npiM30Bps !== undefined && b.npiM30Bps !== null) {
        const sample = b.npiSample ?? 0;
        chips.push(
            `NPI M30 ${(b.npiM30Bps / 100).toFixed(1)}% · ${String(sample)} ${sample === 1 ? 'crédito' : 'créditos'}`,
        );
    }
    return chips;
}

/** El multiplicador ya está repartido en cada regla; se nombra aparte, con su tope si lo hubo. */
export function multiplierNote(b: Breakdown): string | null {
    if (b.multiplierBps === 10_000 && b.multiplierBeforeCapBps === 10_000) return null;
    const names = orEmpty(b.multipliers)
        .map((m) => m.name)
        .join(', ');
    const capped =
        b.multiplierBeforeCapBps !== b.multiplierBps
            ? ` · con tope; sin tope sería ${factor(b.multiplierBeforeCapBps)}`
            : '';
    return `${factor(b.multiplierBps)}${names === '' ? '' : ` (${names})`}, ya incluido en cada regla${capped}`;
}
